import { DataroomTenant } from '@dataroom/tenant/DataroomTenant';
import dataroomConfig from '@dataroom/config';
import baseUrl from '@framework/url/baseUrl';
import { appendSearchParams, buildUrl } from '@framework/url/helpers';
import domain from '@framework/config/domain';
import shouldUseInternalNetwork from '@framework/router/Next/shouldUseInternalNetwork';
import dmPortalUrl from '@dm-portal/url';

export class DataroomUrl {
  public constructor(protected tenant: DataroomTenant | ':tenant') {}

  protected getTenantConfig() {
    const config = dataroomConfig[this.tenant as DataroomTenant];
    if (!config) {
      throw new Error(`DataroomUrl: invalid tenant "${ this.tenant }"`);
    }
    return config;
  }

  public getBaseUrl(...uris: string[]): string {
    return buildUrl(domain.protocol, this.getTenantConfig().hostname, ...uris);
  }

  public getUrl(...uris: string[]): string {
    return this.getBaseUrl(...uris);
  }

  public getProxyApiRpcPath(): string {
    return `/proxy/${ this.tenant }/rpc`;
  }

  public getApiUrl(...uris: string[]): string {
    return baseUrl.getApiUrl(this.tenant, ...uris);
  }

  public getRpcUrl(): string {
    if (shouldUseInternalNetwork()) {
      return this.getInternalRpcUrl();
    }

    if (
      typeof window !== 'undefined' &&
      window.location.hostname === this.getTenantConfig().hostname &&
      window.location.hostname !== domain.usersDomain
    ) {
      return this.getUrl(this.getProxyApiRpcPath());
    }

    return this.getApiUrl('/rpc');
  }

  public getInternalRpcUrl(): string {
    return `${ this.getTenantConfig().api.internal }/rpc`;
  }

  public getDirectRpcUrl(): string {
    return baseUrl.getRestUrl(this.tenant, '/rpc');
  }

  public getFileUrl(...uris: string[]): string {
    return this.getApiUrl(...uris);
  }

  public getAssetsLocation(...uris: string[]): string {
    return this.getUrl(baseUrl.getAssetsLocation(...uris));
  }

  public getExploreSetsABSUrl(): string {
    return this.getUrl('/explore-sets/abs');
  }

  public getExploreSetsCorporateUrl(): string {
    return this.getUrl('/explore-sets/corporate');
  }

  public getHowItWorksUrl(): string {
    return this.getUrl('/how-it-works');
  }

  public getSavingsGuaranteeFootnotePath(): string {
    return '/features#savings-guarantee';
  }

  public getSavingsGuaranteeFootnoteUrl(): string {
    return this.getUrl(this.getSavingsGuaranteeFootnotePath());
  }

  public getHelpUrl(): string {
    return buildUrl(typeof window !== 'undefined' ? window.location.protocol : 'https:', 'vdr-help.finsight.com');
  }

  public getUploadLogoUrl(): string {
    return this.getApiUrl(`/api/v1/${ this.getTenantConfig().options.tenantPathName }/logo`);
  }

  public getAccessRestrictedPath(
    dataroomName: string, notificationMode: boolean = false, byCountry: boolean = false,
  ): string {
    return `/${ this.tenant }/access-restricted/${ dataroomName }?notification=${ notificationMode }?byCountry=${ byCountry }`;
  }

  public getAccessRestrictedByIpPath(
    dataroomName: string,
    restrictedIp: string,
    countryId: string,
  ): string {
    return `/${ this.tenant }/access-restricted-by-ip/${ dataroomName }?restrictedIp=${ restrictedIp }?countryId=${ countryId }`;
  }

  public getAccessRestrictedUrl(
    dataroomDisplayName: string, notificationMode: boolean = false, byCountry: boolean = false,
  ): string {
    return dmPortalUrl.getUrl(
      this.getAccessRestrictedPath(encodeURIComponent(dataroomDisplayName), notificationMode, byCountry),
    );
  }

  public getAccessRestrictedByIdUrl(
    dataroomName: string,
    restrictedIp: string,
    countryId: string,
  ): string {
    return dmPortalUrl.getUrl(
      this.getAccessRestrictedByIpPath(encodeURIComponent(dataroomName), restrictedIp, countryId),
    );
  }

  public getAccessRequestPath(dataroomId: number | ':dataroomId', dataroomName: string): string {
    return `/${ this.tenant }/auto_send_access_request/${ dataroomId }/${ dataroomName }`;
  }

  public getAccessRequestUrl(dataroomId: number, dataroomDisplayName: string): string {
    return dmPortalUrl.getUrl(
      this.getAccessRequestPath(dataroomId, encodeURI(encodeURIComponent(dataroomDisplayName))),
    );
  }

  public getDataroomUrl(dataroomName: string): string {
    return this.getUrl(`/${ this.getTenantConfig().options.tenantUrlPrefix }/${ dataroomName }`);
  }

  public getDataroomListTwoFactorRedirectUrl(entityName: string, callbackUrl: string): string {
    const {
      protocol,
      hostname,
      pathname,
    } = new URL(dmPortalUrl.getUrl(this.tenant));

    return appendSearchParams(buildUrl(protocol, hostname, pathname), {
      dataroomUrl: callbackUrl,
      showTwoFactor: 1,
      dataroomName: entityName,
    });
  }
}

export default (tenant: DataroomTenant | ':tenant') => new DataroomUrl(tenant);
